import { FC, useEffect, useRef, useState } from 'react';
import { Slide, SlideProps, CardContent, CardContentProps, ButtonBase, ButtonBaseProps } from '@mui/material';

export interface FlippingCardContentProps extends CardContentProps {
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Specifies if detail should be flipped on card hover
   * @default true
   */
  flipOnHover?: boolean;
  /**
   * Specifies if detail should be flipped
   * @default false
   */
  isFlipped?: boolean;
  /**
   * Properties applied to wrapper component
   */
  containerProps?: ButtonBaseProps;
  /**
   * Properties applied to <Slide /> component
   */
  slideProps?: Omit<SlideProps, 'children'>;
}

/**
 * Sub-element for Card component for flipping content
 */
export const FlippingCardContent: FC<FlippingCardContentProps> = ({
  children,
  disabled,
  isFlipped = false,
  flipOnHover = true,
  sx,
  className,
  containerProps,
  slideProps,
  ...rest
}) => {
  const { sx: containerSx, ...restContainer } = containerProps || {};
  const containerRef = useRef(null);

  const [flipped, setFlipped] = useState(isFlipped);
  /**
   * Detect controlled state
   */
  useEffect(() => {
    setFlipped(isFlipped);
  }, [isFlipped]);

  const handleMouseEnter = () => !disabled && flipOnHover && setFlipped(true);
  const handleMouseLeave = () => !disabled && flipOnHover && setFlipped(false);

  return (
    <ButtonBase
      sx={{
        display: 'block',
        textAlign: 'left',
        position: 'absolute',
        inset: '0px',
        overflow: 'hidden',
        borderRadius: 'inherit',
        ...(!flipOnHover && !isFlipped
          ? {
              pointerEvents: 'none',
            }
          : {}),
        ...containerSx,
      }}
      component="div"
      ref={containerRef}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-hidden={!flipped}
      className={['FlowFlippingCardContent-root', className].filter(Boolean).join(' ')}
      {...restContainer}
    >
      <Slide in={flipped} direction="up" container={containerRef.current} {...slideProps}>
        <CardContent
          sx={{
            position: 'absolute',
            inset: '0',
            backgroundColor: 'surface.primary',
            overflow: 'auto',
            ...sx,
          }}
          {...rest}
        >
          {children}
        </CardContent>
      </Slide>
    </ButtonBase>
  );
};
